import { Fragment, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, RevealOnView } from '@/components/ui';
import { REVEAL_TIMING, useInView } from '@/utils/animation';
import { cn } from '@/utils/cn';

const ROW_KEYS = [
  { number: '01', key: 'trueformEdge' },
  { number: '02', key: 'aquagrip' },
  { number: '03', key: 'multiBand' },
  { number: '04', key: 'triChannel' },
] as const;

const HEADING_FADE_MS = 800;
// Content starts 25% into the heading fade — matches the products-page
// cascade pacing.
const CONTENT_DELAY_MS = Math.round(HEADING_FADE_MS * 0.25);

export function TechnologySection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const visible = useInView(sectionRef);

  return (
    <section
      ref={sectionRef}
      aria-labelledby="technology-heading"
      className="w-full bg-clearlab-ink px-6 py-30 text-clearlab-oat md:px-14 md:py-30 lg:px-20 lg:py-60 xl:px-30 xl:py-60"
    >
      <div className="mx-auto flex w-full max-w-content-865 flex-col gap-20 lg:mx-0 lg:max-w-none lg:gap-30 xl:mx-auto xl:max-w-content-865">
        <h2
          id="technology-heading"
          className={cn(
            'text-center text-heading-32 font-normal tracking-tight md:text-center lg:text-center',
            'transition-opacity duration-[800ms] ease-out',
            visible ? 'opacity-100' : 'opacity-0',
          )}
        >
          {t('home.technology.heading')}
        </h2>

        <div
          style={{
            transform: visible ? 'translateY(0)' : `translateY(${REVEAL_TIMING.offsetPx}px)`,
            opacity: visible ? 1 : 0,
            transition: `transform ${REVEAL_TIMING.durationMs}ms ${REVEAL_TIMING.ease} ${CONTENT_DELAY_MS}ms, opacity ${REVEAL_TIMING.durationMs}ms ${REVEAL_TIMING.ease} ${CONTENT_DELAY_MS}ms`,
          }}
          className="flex flex-col gap-20 lg:gap-30"
        >
          {ROW_KEYS.map(({ number, key }, idx) => {
            const body = t(`home.technology.rows.${key}.body`, { returnObjects: true }) as string[];
            return (
              <Fragment key={number}>
                <article className="flex flex-col gap-10 lg:flex-row lg:items-start lg:justify-between">
                  <header className="flex shrink-0 flex-col lg:w-60">
                    <span className="text-label-12 font-semibold text-clearlab-oat/60">
                      {number}
                    </span>
                    <h3 className="text-heading-16 font-bold tracking-tight">
                      {t(`home.technology.rows.${key}.title`)}
                    </h3>
                  </header>
                  <div className="flex flex-col gap-10 text-label-16 lg:max-w-[600px] lg:flex-1 xl:max-w-[520px]">
                    {body.map((paragraph, i) => (
                      <p key={i} className="leading-[44px]">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </article>
                {idx < ROW_KEYS.length - 1 ? (
                  <hr className="w-full border-0 border-t border-clearlab-divider-mid/60" />
                ) : null}
              </Fragment>
            );
          })}
        </div>

        <RevealOnView from="left" fadeStart={0.3}>
          <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:justify-between lg:gap-10">
            <div aria-hidden className="hidden shrink-0 lg:block lg:w-60" />
            <div className="lg:max-w-[600px] lg:flex-1 xl:max-w-[520px]">
              <Button
                to="/technology"
                variant="outlined"
                size="large"
                tone="blue-on-dark"
                iconRight
              >
                {t('home.technology.cta')}
              </Button>
            </div>
          </div>
        </RevealOnView>
      </div>
    </section>
  );
}
